/**
 * Local SQLite adapters for panoramas.
 * Mirrors the Convex api.panoramas.* interface.
 */
import { localDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocalPanorama {
  _id: string;
  project_id: string;
  name: string;
  file_url?: string;
  private_file_url?: string;
  thumbnail_url?: string;
  thumbnail_url_webp?: string;
  width?: number;
  height?: number;
  file_size?: number;
  order?: number;
  file_status?: string;
  processing_status?: string;
  gps_location?: Record<string, unknown>;
  data?: Record<string, unknown>;
  status?: string;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

const SELECT_PANORAMA = `
  SELECT id as _id, id, project_id, name,
    storage_path as private_file_url,
    storage_path as file_url,
    thumbnail_path as thumbnail_url,
    thumbnail_path as thumbnail_url_webp,
    width, height, file_size, order_index as \`order\`,
    file_status, processing_status, metadata as data,
    status,
    created_at, updated_at
  FROM panoramas`;

// ─── Queries ──────────────────────────────────────────────────────────────────

export function usePanoramasByProject(
  projectId: string | undefined,
  craftspaceId: string | undefined,
): LocalPanorama[] | undefined {
  return useLocalQuery<LocalPanorama>(
    `${SELECT_PANORAMA}
     WHERE project_id = ? AND (status = 'active' OR status IS NULL)
     ORDER BY order_index ASC, created_at ASC`,
    [projectId ?? ""],
    ["panoramas"],
    ["data", "gps_location"],
    craftspaceId,
  );
}

export function usePanorama(
  id: string | undefined,
  craftspaceId: string | undefined,
): LocalPanorama | null | undefined {
  return useLocalGet<LocalPanorama>(
    `${SELECT_PANORAMA} WHERE id = ?`,
    [id ?? ""],
    ["panoramas"],
    ["data"],
    craftspaceId,
  );
}

export function usePanoramasNeedingThumbnails(
  craftspaceId: string | undefined,
  limit = 10,
): LocalPanorama[] | undefined {
  return useLocalQuery<LocalPanorama>(
    `${SELECT_PANORAMA}
     WHERE thumbnail_path IS NULL AND storage_path IS NOT NULL
       AND (status = 'active' OR status IS NULL)
     LIMIT ?`,
    [limit],
    ["panoramas"],
    ["data"],
    craftspaceId,
  );
}

export function useTrashedPanoramas(
  projectId: string | undefined,
  craftspaceId: string | undefined,
): LocalPanorama[] | undefined {
  return useLocalQuery<LocalPanorama>(
    `${SELECT_PANORAMA}
     WHERE project_id = ? AND status = 'deleted'
     ORDER BY updated_at DESC`,
    [projectId ?? ""],
    ["panoramas"],
    ["data"],
    craftspaceId,
  );
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createPanorama(params: {
  project_id: string;
  craftspace_id: string;
  name: string;
  order?: number;
  width?: number;
  height?: number;
  file_size?: number;
  file_url?: string;
  private_file_url?: string;
  thumbnail_url?: string;
  thumbnail_url_webp?: string;
  file_status?: string;
  processing_status?: string;
  gps_location?: Record<string, unknown>;
  data?: Record<string, unknown>;
}): Promise<string> {
  const id = newId();
  const ts = now();

  const storagePath = params.private_file_url ?? params.file_url ?? null;
  const thumbnailPath = params.thumbnail_url ?? null;

  await localDb(params.craftspace_id).run(
    `INSERT INTO panoramas (id, project_id, name, storage_path, thumbnail_path,
       width, height, file_size, order_index, file_status, processing_status, metadata, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      params.project_id,
      params.name,
      storagePath,
      thumbnailPath,
      params.width ?? null,
      params.height ?? null,
      params.file_size ?? null,
      params.order ?? 0,
      params.file_status ?? "ready",
      params.processing_status ?? "idle",
      params.data ? JSON.stringify(params.data) : null,
      ts,
      ts,
    ],
  );
  invalidate("panoramas");
  return id;
}

export async function updatePanorama(params: {
  id: string;
  craftspace_id: string;
  name?: string;
  order?: number;
  width?: number;
  height?: number;
  file_size?: number;
  file_url?: string;
  private_file_url?: string;
  thumbnail_url?: string;
  thumbnail_url_webp?: string;
  file_status?: string;
  processing_status?: string;
  gps_location?: Record<string, unknown>;
  data?: Record<string, unknown>;
}): Promise<void> {
  const { id, craftspace_id, ...fields } = params;
  const setClauses: string[] = ["updated_at = ?"];
  const values: unknown[] = [now()];

  if (fields.name !== undefined) { setClauses.push("name = ?"); values.push(fields.name); }
  if (fields.order !== undefined) { setClauses.push("order_index = ?"); values.push(fields.order); }
  if (fields.width !== undefined) { setClauses.push("width = ?"); values.push(fields.width); }
  if (fields.height !== undefined) { setClauses.push("height = ?"); values.push(fields.height); }
  if (fields.file_size !== undefined) { setClauses.push("file_size = ?"); values.push(fields.file_size); }
  if (fields.file_status !== undefined) { setClauses.push("file_status = ?"); values.push(fields.file_status); }
  if (fields.processing_status !== undefined) { setClauses.push("processing_status = ?"); values.push(fields.processing_status); }
  if (fields.private_file_url !== undefined) { setClauses.push("storage_path = ?"); values.push(fields.private_file_url); }
  else if (fields.file_url !== undefined) { setClauses.push("storage_path = ?"); values.push(fields.file_url); }
  if (fields.thumbnail_url !== undefined) { setClauses.push("thumbnail_path = ?"); values.push(fields.thumbnail_url); }
  if (fields.gps_location !== undefined) { setClauses.push("metadata = json_patch(COALESCE(metadata, '{}'), ?)"); values.push(JSON.stringify({ gps_location: fields.gps_location })); }
  if (fields.data !== undefined) { setClauses.push("metadata = ?"); values.push(JSON.stringify(fields.data)); }

  values.push(id);
  await localDb(craftspace_id).run(`UPDATE panoramas SET ${setClauses.join(", ")} WHERE id = ?`, values);
  invalidate("panoramas");
}

export async function trashPanorama(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("UPDATE panoramas SET status = 'deleted', updated_at = ? WHERE id = ?", [now(), id]);
  invalidate("panoramas", "hotspots");
}

export async function restorePanorama(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("UPDATE panoramas SET status = 'active', updated_at = ? WHERE id = ?", [now(), id]);
  invalidate("panoramas", "hotspots");
}

export async function emptyTrash(projectId: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).transaction([
    {
      sql: `DELETE FROM asset_versions WHERE entity_type = 'panorama' AND entity_id IN
            (SELECT id FROM panoramas WHERE project_id = ? AND status = 'deleted')`,
      params: [projectId],
    },
    {
      sql: `DELETE FROM panoramas WHERE project_id = ? AND status = 'deleted'`,
      params: [projectId],
    },
  ]);
  invalidate("panoramas", "hotspots", "asset_versions");
}

export async function removePanorama(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM panoramas WHERE id = ?", [id]);
  invalidate("panoramas", "hotspots");
}
